import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiUserManager } from '../../enums/api-user-manager.enum';
import { GenericConstants } from 'src/app/core/enums/generic-constants.enum';
import { PwRecovery } from '../../models/admin-user/pw-recovery';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';
import { AuthSocialMedia } from '../../models/auth/auth-social-media';


type AuthProvider = firebase.auth.GoogleAuthProvider | firebase.auth.FacebookAuthProvider;

/**
 * Clase de Error personalizada: Señala al componente que el usuario
 * no existe en el backend y se necesita información adicional (ID Token)
 * para completar el registro.
 */
export class NeedsRegistrationDataError extends Error {
  idToken: string;
  constructor(idToken: string) {
    super('User not found. Additional registration data required.');
    this.name = 'NeedsRegistrationDataError';
    this.idToken = idToken;
  }
}

/**
 * Error personalizado: El email ya está registrado con un proveedor diferente.
 * Firebase no permite autenticarse con múltiples proveedores para el mismo email.
 */
export class AccountExistsWithDifferentCredentialError extends Error {
  email: string;
  existingProvider: string;
  attemptedProvider: string;

  constructor(email: string, existingProvider: string, attemptedProvider: string) {
    super(`This email is already registered with ${existingProvider}. Please sign in using ${existingProvider}.`);
    this.name = 'AccountExistsWithDifferentCredentialError';
    this.email = email;
    this.existingProvider = existingProvider;
    this.attemptedProvider = attemptedProvider;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private http: HttpClient, private afAuth: AngularFireAuth) { }

  sendCredentialForLogin(data: JSON) {
    let datalogin = JSON.stringify(data);
    let dataloginObjetc = JSON.parse(datalogin);
    let urlenconded = new HttpParams()
      .set('email', dataloginObjetc.email)
      .set('password', dataloginObjetc.password);
    let url = environment.baseUrl + ApiUserManager.USER_LOGIN;
    let headers = new HttpHeaders({
      accept: '*/*',
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http.post(url, urlenconded.toString(), { headers: headers });
  }

  isLogged() {
    const xToken = localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN);
    let userValid = false;
    //let userObject = JSON.parse(localStorage.getItem(GenericConstants.STORAGE_LOGIN_DATA));
    if (xToken) {
      userValid = true;
    }
    return userValid;
  }

  sendNewRegister(user: any) {
    let url = environment.baseUrl + ApiUserManager.USER_REGISTER;
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });
    return this.http.post(url, JSON.stringify(user), { headers: headers });
  }

  isValidated(token: string, id: string) {
    let url = environment.baseUrl + ApiUserManager.USER_VALIDATE;
    let urlenconded = new HttpParams()
      .set('token', token)
      .set('userId', id);
    let headers = new HttpHeaders({
      accept: '*/*',
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http.post(url, urlenconded.toString(), { headers: headers });

  }

  sendRecoverPw(email: any) {
    let url = environment.baseUrl + ApiUserManager.SEND_RECOVER;
    let headers = new HttpHeaders({
      accept: '*/*',
      'Content-Type': 'application/json',
    });
    return this.http.post(url, JSON.stringify(email), { headers: headers });
  }

  setNewPassword(pass: PwRecovery) {
    let url = environment.baseUrl + ApiUserManager.NEW_PASS;
    let headers = new HttpHeaders({
      accept: '*/*',
      'Content-Type': 'application/json',
    });
    return this.http.post(url, JSON.stringify(pass), { headers: headers });
  }

  // Tour related methods
  getUserBasicInfo() {
    const headers = this.getAuthHeaders();
    return this.http.get(environment.baseUrl + ApiUserManager.USER_BASIC_INFO, { headers });
  }

  sendWelcomeTour(tourData: { version: number, tour_seen: boolean }) {
    const headers = this.getAuthHeaders();
    return this.http.patch(environment.baseUrl + ApiUserManager.USER_WELCOME_TOUR, tourData, { headers });
  }

  private getAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem(GenericConstants.STORAGE_ACCESS_TOKEN)}`
    });
  }
  //  Sign Up con Google
  signInWithGoogle(): Observable<any> {
    const provider = new firebase.auth.GoogleAuthProvider();
    return this.handleAuthWithBackend(provider);
  }

  /**
   * Helper para crear y configurar el FacebookAuthProvider consistentemente.
   * Añade los scopes necesarios y configura parámetros para forzar re-solicitud de permisos.
   */
  private createFacebookProvider(): firebase.auth.FacebookAuthProvider {
    const provider = new firebase.auth.FacebookAuthProvider();
    provider.addScope('email');
    provider.addScope('public_profile');
    // Forzar reautenticación para asegurar permisos frescos
    provider.setCustomParameters({
      'auth_type': 'rerequest'
    });
    return provider;
  }

  //  Sign Up con Facebook
  signInWithFacebook(): Observable<any> {
    const provider = this.createFacebookProvider();
    return this.handleAuthWithBackend(provider);
  }

  logout(): Observable<void> {
    // Convierte la Promise de Firebase signOut() a un Observable.
    return from(this.afAuth.signOut());
  }

  handleAuthWithBackend(provider: AuthProvider): Observable<any> {
    let socialUser: AuthSocialMedia = {};
    const attemptedProvider = provider instanceof firebase.auth.GoogleAuthProvider ? 'GOOGLE' : 'FACEBOOK';
    socialUser.social_media_type = attemptedProvider;
    console.log('Iniciando login con proveedor social:', socialUser.social_media_type);
    let loginrUrl = environment.baseUrl + ApiUserManager.USER_LOGIN + ApiUserManager.SOCIAL_MEDIA_LOGIN;

    // 1. Iniciar sesión con Firebase y convertir la Promesa a Observable con from()
    return from(this.afAuth.signInWithPopup(provider)).pipe(

      switchMap(credential => {
        if (!credential.user) {
          return throwError(() => new Error('No se pudo obtener el usuario de Firebase.'));
        }

        // 2. Obtener el ID Token
        return from(credential.user.getIdToken(true));
      }),

      // 3. Usar el ID Token para intentar el LOGIN en el Backend
      switchMap(idToken => {
        socialUser.auth_token = idToken;

        // Intenta el login primero
        return this.http.post(loginrUrl, socialUser).pipe(

          // 4. Manejar el error de login
          catchError(error => {
            // Si el backend devuelve 404 o 401: Usuario existe en Firebase, pero no en nuestra BD.
            if (error.status === 404 || error.status === 401) {
              console.warn('Login fallido. Usuario debe registrarse.');

              // 🚨 Lanzamos el error con el token para que el componente lo capture
              return throwError(() => new NeedsRegistrationDataError(idToken));
            }

            // Error genérico
            console.error('Error no manejado en la conexión con el backend:', error);
            return throwError(() => error);
          })
        );
      }),

      // 5. Manejar error de Firebase: email ya existe con otro proveedor
      catchError((error: any) => {
        if (error?.code === 'auth/account-exists-with-different-credential') {
          const email = error.customData?.email || error.email || 'unknown';

          // Consultar los métodos de inicio de sesión existentes para este email
          return from(this.afAuth.fetchSignInMethodsForEmail(email)).pipe(
            switchMap((signInMethods: string[]) => {
              // Mapear los métodos de Firebase a nombres de proveedores amigables
              let existingProvider = 'otro proveedor';

              if (signInMethods.includes('google.com')) {
                existingProvider = 'Google';
              } else if (signInMethods.includes('facebook.com')) {
                existingProvider = 'Facebook';
              } else if (signInMethods.includes('password')) {
                existingProvider = 'Email/Password';
              }

              console.warn(`Email ${email} ya está registrado con ${existingProvider}`);
              const customError = new AccountExistsWithDifferentCredentialError(email, existingProvider, attemptedProvider);
              return throwError(() => customError);
            }),
            catchError(() => {
              // Si falla la consulta, usar mensaje genérico
              console.warn(`Email ${email} ya está registrado con otro proveedor`);
              const fallbackError = new AccountExistsWithDifferentCredentialError(email, 'otro proveedor', attemptedProvider);
              return throwError(() => fallbackError);
            })
          );
        }
        // Re-throw otros errores
        return throwError(() => error);
      })
    );
  }

  //  Sign Up con Social Media 
  signUpWithSocial(socialUser: AuthSocialMedia): Observable<any> {

    console.log('Iniciando registro con proveedor social:', socialUser.social_media_type);
    let registerdUser: AuthSocialMedia = {};
    const attemptedProvider = socialUser.social_media_type || 'UNKNOWN';

    const provider = socialUser.social_media_type === 'GOOGLE' ? new firebase.auth.GoogleAuthProvider() : this.createFacebookProvider();
    const loginUrl = environment.baseUrl + ApiUserManager.USER_LOGIN + ApiUserManager.SOCIAL_MEDIA_LOGIN;
    const registerUrl = environment.baseUrl + ApiUserManager.USER_REGISTER + ApiUserManager.SOCIAL_MEDIA_LOGIN;
    return from(this.afAuth.signInWithPopup(provider)).pipe(

      switchMap(credential => {
        if (!credential.user) {
          return throwError(() => new Error('No se pudo obtener el usuario de Firebase.'));
        }

        // 2. Obtener el ID Token
        console.log('Obteniendo ID Token de Firebase...');
        return from(credential.user.getIdToken(true));
      }),

      // 3. Usar el ID Token para intentar el LOGIN en el Backend
      switchMap(idToken => {

        socialUser.auth_token = idToken;
        registerdUser.social_media_type = socialUser.social_media_type;
        registerdUser.auth_token = socialUser.auth_token;

        // Intenta el registro directamente
        console.log('Enviando datos de registro al backend...');
        return this.http.post(registerUrl, socialUser).pipe(
          switchMap(registerResponse =>
            this.http.post(loginUrl, registerdUser).pipe(
              map((finalResponse) => ({
                origin: 'success-flow',   // ✨ etiqueta de origen
                data: finalResponse
              }))
            ) // return the second call
          ),
          // 4. Manejar el error de login
          catchError(error => {
            // Si el backend devuelve 404 o 401: Usuario existe en Firebase, pero no en nuestra BD.
            const errorCode = error?.error?.code;
            if (errorCode === 700 || errorCode === '700') {
              return this.http.post(loginUrl, registerdUser).pipe(
                map((finalResponse) => ({
                  origin: 'fallback-700',   // ✨ etiqueta de origen
                  data: finalResponse
                }))
              ); // return the second call
            }
            console.error('Error no manejado en la conexión con el backend:', error);
            return throwError(() => error);
          })
        );
      }),

      // 5. Manejar error de Firebase: email ya existe con otro proveedor
      catchError((error: any) => {
        if (error?.code === 'auth/account-exists-with-different-credential') {
          const email = error.customData?.email || error.email || 'unknown';

          // Consultar los métodos de inicio de sesión existentes para este email
          return from(this.afAuth.fetchSignInMethodsForEmail(email)).pipe(
            switchMap((signInMethods: string[]) => {
              // Mapear los métodos de Firebase a nombres de proveedores amigables
              let existingProvider = 'otro proveedor';

              if (signInMethods.includes('google.com')) {
                existingProvider = 'Google';
              } else if (signInMethods.includes('facebook.com')) {
                existingProvider = 'Facebook';
              } else if (signInMethods.includes('password')) {
                existingProvider = 'Email/Password';
              }

              console.warn(`Email ${email} ya está registrado con ${existingProvider}`);
              const customError = new AccountExistsWithDifferentCredentialError(email, existingProvider, attemptedProvider);
              return throwError(() => customError);
            }),
            catchError(() => {
              // Si falla la consulta, usar mensaje genérico
              console.warn(`Email ${email} ya está registrado con otro proveedor`);
              const fallbackError = new AccountExistsWithDifferentCredentialError(email, 'otro proveedor', attemptedProvider);
              return throwError(() => fallbackError);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  //  Sign Up with existing token (google or facebook from login)
  signUpWithExistingToken(socialUser: AuthSocialMedia): Observable<any> {
    console.log('Registering with existing token:', socialUser.social_media_type);
    const registerUrl = environment.baseUrl + ApiUserManager.USER_REGISTER + ApiUserManager.SOCIAL_MEDIA_LOGIN;

    // Direct registration with the token already provided
    return this.http.post(registerUrl, socialUser).pipe(
      catchError(error => {
        console.error('Error during registration with existing token:', error);
        return throwError(() => error);
      })
    );
  }

  //  Login with an existing social token (for building status modal)
  loginWithExistingToken(socialUser: AuthSocialMedia): Observable<any> {
    console.log('Logging in with existing token for provider:', socialUser.social_media_type);
    const loginUrl = environment.baseUrl + ApiUserManager.USER_LOGIN + ApiUserManager.SOCIAL_MEDIA_LOGIN;

    return this.http.post(loginUrl, socialUser).pipe(
      catchError(error => {
        console.error('Error during social login with existing token:', error);
        return throwError(() => error);
      })
    );
  }

  //  Create site for existing social user (user logged in from login page but has no site)
  createSiteForSocialUser(siteData: any): Observable<any> {
    console.log('Creating site for existing social user:', siteData);
    const createSiteUrl = environment.baseUrl + ApiUserManager.USER_REGISTER + ApiUserManager.SOCIAL_MEDIA_LOGIN;

    // Use the existing token from localStorage
    return this.http.post(createSiteUrl, siteData).pipe(
      catchError(error => {
        console.error('Error creating site for social user:', error);
        return throwError(() => error);
      })
    );
  }
}
